import { Link } from 'react-router-dom';
import type { ContractDashboardRow, VendorBankDetailsStatuses } from '@urbanx/agx.campaigns.client';
import type { GetAllAgentsDetailsProjection } from '@urbanx/agx.agencies.client';
import {
  AgxAvatar,
  AgxBadge,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  Icon_FileTextOutline,
  SortOrder,
} from '@urbanx/agx.ui-components2';
import { cleanTwoLineAddress, PropertyAddress } from '@urbanx/agx-ui-components';
import LeadAgentCell from 'components/LeadAgentCell';
import { createAgencyLookup, getAgencyInitials } from 'utils/agencyUtils';
import {
  formatSettlementDateBadge,
  formatVendorBankDetails,
  statusBadgeColor,
} from './ContractDashboardBadges';
import './ContractsDashboard.scss';

interface CampaignsDashboardTableProps {
  rows: ContractDashboardRow[];
  agencyLookup: ReturnType<typeof createAgencyLookup>;
  agentLookup: Record<string, GetAllAgentsDetailsProjection>;
  isPending: boolean;
  isAgenciesPending: boolean;
  getContractLink: (index: number) => string | undefined;
  onSortSettlement: (sortOrder: SortOrder) => void;
}

const CampaignsDashboardTable = ({
  rows,
  agencyLookup,
  agentLookup,
  isPending,
  isAgenciesPending,
  getContractLink,
  onSortSettlement,
}: CampaignsDashboardTableProps) => {
  const dashboardColumns: (string | IColumnConfig)[] = [
    { name: 'Contract', maxColWidth: 250 },
    { name: 'Address', maxColWidth: 250 },
    'Agency',
    'Lead Agent',
    'Status',
    {
      name: 'Settlement',
      sortable: true,
      sortFunction: onSortSettlement,
      defaultSortOrder: 1,
    },
    { name: 'Vendor Bank Details', align: 'right' },
  ];

  const tableRows = rows.map((row, index) => {
    const agencyName = row.agencyId ? agencyLookup[row.agencyId]?.name : undefined;
    const agencyInitials = getAgencyInitials(agencyName);
    const leadAgent = row.leadAgentId ? agentLookup[row.leadAgentId] : undefined;
    const cleanAddress = row.address
      ? cleanTwoLineAddress(row.address as PropertyAddress)
      : ['Unknown address'];
    const [addressLineOne, addressLineTwo] = Array.isArray(cleanAddress)
      ? cleanAddress
      : [cleanAddress];

    return {
      Contract: (
        <AgxSpacer direction="horizontal" contentAlignment="center" gap={'2xs'}>
          <div className="contractDashboard_iconContainer">
            <Icon_FileTextOutline height={14} width={14} />
          </div>
          <div style={{ minWidth: 0 }}>
            <AgxTypography
              id={`campaign-dashboard-status-${index}`}
              size="small"
              type="body_ui"
              style={{
                color: 'var(--text-secondary)',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {row.contractName}
            </AgxTypography>
          </div>
        </AgxSpacer>
      ),
      Address: (
        <AgxSpacer contentAlignment="start" gap={'3xs'}>
          <AgxTypography
            id={`campaign-dashboard-status-${index}`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
            overflowEllipsis
          >
            {addressLineOne ?? 'Unknown address'}
          </AgxTypography>
          <AgxTypography
            id={`campaign-dashboard-status-${index}`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
            overflowEllipsis
          >
            {addressLineTwo ?? ''}
          </AgxTypography>
        </AgxSpacer>
      ),
      Agency: (
        <AgxSpacer direction="horizontal" gap="spacing-md" contentAlignment="center">
          {row.agencyId && agencyLookup[row.agencyId]?.logoUrl && (
            <AgxAvatar
              type="image"
              size="sm"
              imageUri={agencyLookup[row.agencyId]?.logoUrl ?? ''}
              backgroundColor={agencyLookup[row.agencyId]?.backgroundColor}
            />
          )}
          {row.agencyId && !agencyLookup[row.agencyId]?.logoUrl && (
            <AgxAvatar type="text-dark" size="sm" text={agencyInitials ?? 'UA'} />
          )}

          <AgxTypography
            id={`campaign-dashboard-agency-${index}`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
          >
            {agencyName ?? '--'}
          </AgxTypography>
        </AgxSpacer>
      ),
      'Lead Agent': (
        <LeadAgentCell id={`contract-dashboard-lead-agent-${index}`} agent={leadAgent} />
      ),
      Status: (
        <AgxBadge
          type="colourfill"
          variant="normal"
          size="md"
          text={row.status}
          colour={statusBadgeColor(row.status)}
        />
      ),
      Settlement: formatSettlementDateBadge(row.settlementDate, row.status),
      'Vendor Bank Details': formatVendorBankDetails(
        row.vendorBankDetailsStatus as unknown as VendorBankDetailsStatuses
      ),
    };
  });

  return (
    <AgxSpacer direction="horizontal" fill>
      <AgxSpacer direction="vertical" gap={'sm'} fill>
        <AgxTable
          columns={dashboardColumns}
          rows={tableRows}
          variant={!isPending && tableRows.length ? 'default' : 'minimal'}
          showLoadingState={isPending && tableRows.length === 0}
          tableStyle="square"
          stickyHeader
          noDataLabel={isAgenciesPending || isPending ? 'Loading...' : 'No contract found.'}
          noDataGlyphType="coin"
          renderRowLink={(index, linkProps) => {
            if (isPending) return;

            const to = getContractLink(index);
            return to ? (
              <Link
                {...linkProps}
                to={to}
                aria-label={rows[index].contractName ?? 'Open contract'}
              />
            ) : undefined;
          }}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default CampaignsDashboardTable;
